import moment from "moment-timezone";
import Styles from "./Weather.module.scss";
import { Text } from "src/components/atoms/Text/Text";
import { RadarMap } from "./RadarMap";
import * as Event from "src/state/modules/event";
import { useGetForecastQuery, useGetAlertsQuery } from "src/state/modules/weather";
import {
  computeStormRisk,
  describeWeatherCode,
  findGameTimeIndex,
  NwsAlertProperties,
} from "src/utils/weather";

const round = (n: number | undefined) => (n == null ? "—" : Math.round(n));

export const WeatherBlock = ({ event }: { event: Event.Event }) => {
  const coords = event.location?.coordinates;
  const lat = coords?.latitude;
  const lon = coords?.longitude;
  const hasCoords = lat != null && lon != null && (lat !== 0 || lon !== 0);

  // Hooks must run unconditionally; skip the network call when we have no point.
  const { data: forecast, isLoading } = useGetForecastQuery(
    { lat: lat as number, lon: lon as number },
    { skip: !hasCoords }
  );
  const { data: alertsData } = useGetAlertsQuery({ lat: lat as number, lon: lon as number }, { skip: !hasCoords });

  if (!hasCoords) return null;

  // Hide weather on games that finished more than a few hours ago — it's only
  // useful for upcoming/live games.
  const start = moment.tz(event.start, event.timezone || moment.tz.guess());
  if (start.isBefore(moment().subtract(6, "hours"))) return null;

  if (isLoading) {
    return (
      <div className={Styles.card}>
        <Text size={"base"} weight={"xbold"} block bottomMargin>
          Weather
        </Text>
        <Text size={"base"} color={"cool-grey"} block>
          Loading forecast…
        </Text>
      </div>
    );
  }

  if (!forecast?.current) return null;

  const current = forecast.current;
  const hourly = forecast.hourly;
  const gameIdx = findGameTimeIndex(hourly?.time, event.start, event.timezone || "UTC");
  const hasGameTime = gameIdx >= 0 && !!hourly;

  // Risk is evaluated at game time when we have that hour; otherwise now.
  const alerts: NwsAlertProperties[] = (alertsData?.features ?? []).map((f) => f.properties);
  const riskCape = hasGameTime ? hourly!.cape?.[gameIdx] : undefined;
  const riskLpi = hasGameTime ? hourly!.lightning_potential?.[gameIdx] : undefined;
  const riskPrecip = hasGameTime ? hourly!.precipitation_probability?.[gameIdx] : undefined;
  const riskCode = hasGameTime ? hourly!.weather_code?.[gameIdx] : current.weather_code;
  const risk = computeStormRisk({
    cape: riskCape,
    lightningPotential: riskLpi,
    precipProbability: riskPrecip,
    weatherCode: riskCode,
    alerts,
  });

  const currentDesc = describeWeatherCode(current.weather_code);
  const gameDesc = hasGameTime ? describeWeatherCode(hourly!.weather_code[gameIdx]) : undefined;

  return (
    <div className={Styles.card}>
      <div className={Styles.header}>
        <Text size={"base"} weight={"xbold"} block>
          Weather
        </Text>
        <span className={Styles.badge} data-risk={risk.level}>
          <span aria-hidden>{risk.icon}</span>
          <Text size={"small"} weight={"bold"} color={risk.color}>
            {risk.label}
          </Text>
        </span>
      </div>

      {risk.drivers.length > 0 && (
        <Text size={"xsmall"} color={"cool-grey-dark"} block className={Styles.drivers}>
          {risk.drivers.join(" · ")}
        </Text>
      )}

      {/* Active NWS severe-weather alerts (US). */}
      {alerts
        .filter((a) => /(thunderstorm|tornado|flood|wind|hail)/i.test(a.event))
        .map((a, idx) => (
          <div key={idx} className={Styles.alert}>
            <Text size={"small"} weight={"bold"} color={"dark-red"} block>
              {`⚠️ ${a.event}`}
            </Text>
            {a.headline && (
              <Text size={"xsmall"} color={"cool-grey-dark"} block>
                {a.headline}
              </Text>
            )}
          </div>
        ))}

      <div className={Styles.conditions}>
        <div className={Styles.now}>
          <span className={Styles.bigIcon} aria-hidden>
            {currentDesc.icon}
          </span>
          <div>
            <Text size={"xlarge"} weight={"xbold"} block>
              {`${round(current.temperature_2m)}°`}
            </Text>
            <Text size={"small"} color={"cool-grey-dark"} block>
              {`${currentDesc.label} · feels ${round(current.apparent_temperature)}°`}
            </Text>
          </div>
        </div>

        {hasGameTime && (
          <div className={Styles.gameTime}>
            <Text size={"xsmall"} color={"cool-grey-dark"} weight={"bold"} block textTransform={"uppercase"}>
              {`At game time · ${start.format("h:mm A")}`}
            </Text>
            <div className={Styles.gameTimeRow}>
              <span aria-hidden>{gameDesc?.icon}</span>
              <Text size={"base"} weight={"bold"}>
                {`${round(hourly!.temperature_2m[gameIdx])}°`}
              </Text>
              <Text size={"small"} color={"cool-grey-dark"}>
                {`💧 ${round(hourly!.precipitation_probability[gameIdx])}%`}
              </Text>
              <Text size={"small"} color={"cool-grey-dark"}>
                {`💨 ${round(hourly!.wind_speed_10m[gameIdx])} mph`}
              </Text>
            </div>
          </div>
        )}
      </div>

      <div className={Styles.stats}>
        <Stat label={"Wind"} value={`${round(current.wind_speed_10m)} mph`} />
        <Stat label={"Gusts"} value={`${round(current.wind_gusts_10m)} mph`} />
        <Stat label={"Humidity"} value={`${round(current.relative_humidity_2m)}%`} />
      </div>

      <RadarMap lat={lat} lon={lon} timezone={event.timezone || moment.tz.guess()} />
    </div>
  );
};

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div className={Styles.stat}>
    <Text size={"small"} weight={"bold"} block>
      {value}
    </Text>
    <Text size={"xsmall"} color={"cool-grey"} block>
      {label}
    </Text>
  </div>
);
